import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Sparkles, Plus, LogOut, Calendar, Image, QrCode, Trash2, ExternalLink, Monitor, Shield, Crown, CreditCard, Lock, Unlock, Key, Copy, Check, X, Globe, Eye } from 'lucide-react';
import { motion } from 'framer-motion';
import { VibeIcon } from '../lib/moodIcons';
import formatEventDate from '../lib/formatEventDate';
import { SkeletonGrid, EmptyState } from '../components/Skeleton';
import SiteHeader from '../components/SiteHeader';
import FooterExpanded from '../components/landing/FooterExpanded';
import { useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [auras, setAuras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [apiKeys, setApiKeys] = useState([]);
  const [showApiKeys, setShowApiKeys] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyResult, setNewKeyResult] = useState(null);
  const [keyCopied, setKeyCopied] = useState(false);
  const [keyLoading, setKeyLoading] = useState(false);
  const [archiveFilter, setArchiveFilter] = useState('active');

  useEffect(() => {
    fetchAuras();
  }, []);

  const fetchAuras = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/auras`, {
        withCredentials: true
      });
      setAuras(data);
    } catch (error) {
      console.error('Error fetching auras:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleDeleteAura = async (auraId) => {
    try {
      await axios.delete(`${API_URL}/api/auras/${auraId}`, {
        withCredentials: true
      });
      setAuras(auras.filter(a => a.id !== auraId));
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting aura:', error);
    }
  };

  const handleToggleVault = async (auraId) => {
    try {
      await axios.post(`${API_URL}/api/auras/${auraId}/vault`, {}, { withCredentials: true });
      fetchAuras();
    } catch (error) {
      console.error('Error toggling vault:', error);
    }
  };

  const fetchApiKeys = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/keys`, { withCredentials: true });
      setApiKeys(data);
    } catch {
      setApiKeys([]);
    }
  };

  const handleGenerateKey = async () => {
    if (!newKeyName.trim()) return;
    setKeyLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/keys`, { name: newKeyName.trim() }, { withCredentials: true });
      setNewKeyResult(data);
      setNewKeyName('');
      fetchApiKeys();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to generate key');
    } finally {
      setKeyLoading(false);
    }
  };

  const handleRevokeKey = async (keyId) => {
    try {
      await axios.delete(`${API_URL}/api/keys/${keyId}`, { withCredentials: true });
      setApiKeys(prev => prev.filter(k => k.id !== keyId));
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to revoke key');
    }
  };

  const handleCopyKey = (key) => {
    navigator.clipboard.writeText(key);
    setKeyCopied(true);
    setTimeout(() => setKeyCopied(false), 2000);
  };

  const formatDate = (aura) => formatEventDate(aura, { style: 'short' });

  const getDaysUntilExpiry = (expiresAt) => {
    if (!expiresAt) return null;
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diff = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
    return diff;
  };

  return (
    <div className="min-h-screen bg-midnight">
      <SiteHeader />
      {/* Secondary actions bar — preserves role-based links and test ids */}
      <div className="border-b border-champagne/10 bg-midnight/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2 flex items-center justify-end gap-2 sm:gap-4 flex-wrap">
          {user && ['support', 'billing', 'auditor', 'admin', 'superadmin'].includes(user.role) && (
            <Link to="/admin" className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-aura-gold/10 text-aura-gold text-sm hover:bg-aura-gold/20 transition-colors" data-testid="admin-panel-link">
              <Shield className="w-4 h-4" />
              <span className="hidden sm:inline">Admin</span>
            </Link>
          )}
          {user?.plan && user.plan !== 'free' && (
            <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-champagne/10 text-champagne text-xs font-medium">
              <Crown className="w-3 h-3" />
              {user.plan}
            </span>
          )}
          {(!user?.plan || user.plan === 'free') && (
            <Link to="/pricing" className="flex items-center gap-1 text-champagne text-sm hover:text-champagne-light transition-colors" data-testid="upgrade-plan-link">
              <CreditCard className="w-4 h-4" />
              <span className="hidden sm:inline">Upgrade</span>
            </Link>
          )}
          <Link to="/account/integrations" className="text-text-muted hover:text-champagne text-sm transition-colors" data-testid="nav-integrations">Integrations</Link>
          {user?.org_id && (
            <>
              <Link to="/org/dashboard" className="text-text-muted hover:text-champagne text-sm transition-colors" data-testid="nav-org-dashboard">Org</Link>
              <Link to="/org/settings" className="text-text-muted hover:text-champagne text-sm transition-colors" data-testid="nav-org-settings">Org settings</Link>
              <Link to="/agency" className="text-text-muted hover:text-champagne text-sm transition-colors" data-testid="nav-agency">Agency</Link>
            </>
          )}
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-text-muted hover:text-error transition-colors text-sm"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" />
            <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl font-serif font-medium text-text-light">
              {t('dashboard.yourAuras')}
            </h1>
            <p className="text-text-muted mt-1">
              {t('dashboard.subtitle')}
            </p>
          </div>
          <Link
            to="/create-aura"
            className="btn-champagne flex items-center gap-2"
            data-testid="create-aura-btn"
          >
            <Plus className="w-5 h-5" />
            {t('dashboard.createNew')}
          </Link>
        </div>

        {/* Active / Archived tabs */}
        <div className="flex items-center gap-2 mb-6" role="tablist">
          <button
            role="tab"
            aria-selected={archiveFilter === 'active'}
            onClick={() => setArchiveFilter('active')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              archiveFilter === 'active'
                ? 'bg-champagne text-midnight'
                : 'bg-midnight-light/50 text-text-muted hover:text-champagne'
            }`}
            data-testid="filter-active-tab"
          >
            {t('dashboard.active.toggle')}
          </button>
          <button
            role="tab"
            aria-selected={archiveFilter === 'archived'}
            onClick={() => setArchiveFilter('archived')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              archiveFilter === 'archived'
                ? 'bg-champagne text-midnight'
                : 'bg-midnight-light/50 text-text-muted hover:text-champagne'
            }`}
            data-testid="filter-archived-tab"
          >
            {t('dashboard.archived.toggle')}
          </button>
        </div>

        {/* Auras Grid */}
        {(() => {
          const filteredAuras = auras.filter(a =>
            archiveFilter === 'archived' ? a.is_archived : !a.is_archived
          );
          return loading ? (
          <SkeletonGrid count={3} />
        ) : filteredAuras.length === 0 ? (
          archiveFilter === 'archived' ? (
            <EmptyState
              icon={Sparkles}
              title={t('dashboard.archived.emptyTitle')}
              description={t('dashboard.archived.emptyDescription')}
            />
          ) : (
          <EmptyState
            icon={Sparkles}
            title={t('dashboard.empty.title')}
            description={t('dashboard.empty.description')}
            cta={
              <Link
                to="/create-aura"
                className="btn-champagne inline-flex items-center gap-2"
                data-testid="empty-create-aura-btn"
              >
                <Plus className="w-5 h-5" />
                {t('dashboard.empty.cta')}
              </Link>
            }
          />
          )
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {filteredAuras.map((aura, index) => {
              const daysLeft = getDaysUntilExpiry(aura.expires_at);
              const fadeProgress = aura.saudade_progress || 0;
              const isExpired = aura.is_expired;
              const isVaulted = aura.is_vaulted;
              const isArchived = aura.is_archived;
              const blurAmount = isVaulted ? 0 : Math.min(fadeProgress / 10, 8);

              return (
                <motion.div
                  key={aura.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`aura-card rounded-xl overflow-hidden group relative ${isExpired && !isVaulted ? 'ring-1 ring-error/30' : ''} ${isArchived ? 'opacity-70' : ''}`}
                  data-testid={`aura-card-${aura.id}`}
                >
                  {/* Archived Badge */}
                  {isArchived && (
                    <div className="absolute top-3 right-3 z-20 flex items-center gap-1 bg-midnight/80 text-text-muted px-2 py-0.5 rounded-full text-xs font-medium border border-champagne/20" data-testid={`archived-badge-${aura.id}`}>
                      Archived (read-only)
                    </div>
                  )}

                  {/* Saudade Fade Overlay */}
                  {fadeProgress > 0 && !isVaulted && (
                    <div
                      className="absolute inset-0 z-10 pointer-events-none rounded-xl"
                      style={{
                        backdropFilter: `blur(${blurAmount}px)`,
                        background: `rgba(26, 27, 46, ${fadeProgress / 200})`,
                      }}
                    />
                  )}

                  {/* Vault Badge */}
                  {isVaulted && (
                    <div className="absolute top-3 left-3 z-20 flex items-center gap-1 bg-champagne/90 text-midnight px-2 py-0.5 rounded-full text-xs font-bold" data-testid={`vault-badge-${aura.id}`}>
                      <Lock className="w-3 h-3" /> Vaulted
                    </div>
                  )}

                  {/* Cover */}
                  <div 
                    className="h-40 relative"
                    style={{ background: `linear-gradient(135deg, ${aura.primary_color}40, ${aura.accent_color}40)` }}
                  >
                    {aura.cover_image ? (
                      <img
                        src={aura.cover_image}
                        alt={aura.title}
                        className="w-full h-full object-cover opacity-70"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Sparkles className="w-12 h-12 text-champagne/30" />
                      </div>
                    )}
                    
                    {/* Vibe badge */}
                    <div className={`absolute ${isVaulted ? 'top-10' : 'top-3'} left-3`}>
                      <span className="mood-badge inline-flex items-center gap-1">
                        <VibeIcon vibe={aura.vibe} className="w-3.5 h-3.5" /> {aura.vibe}
                      </span>
                    </div>

                    {/* Saudade Timer */}
                    {daysLeft !== null && daysLeft <= 30 && !isVaulted && (
                      <div className="absolute top-3 right-3 bg-midnight/80 backdrop-blur-sm px-2 py-1 rounded text-xs">
                        {daysLeft > 0 ? (
                          <span className="text-champagne">{daysLeft}d left</span>
                        ) : (
                          <span className="text-error">Expired</span>
                        )}
                      </div>
                    )}

                    {/* Saudade Progress Bar */}
                    {fadeProgress > 0 && !isVaulted && (
                      <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/10">
                        <div
                          className={`h-full transition-all ${fadeProgress > 70 ? 'bg-error' : fadeProgress > 40 ? 'bg-champagne' : 'bg-success'}`}
                          style={{ width: `${fadeProgress}%` }}
                        />
                      </div>
                    )}

                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-midnight/80 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3 z-20">
                      <Link
                        to={`/aura/${aura.id}`}
                        className="w-10 h-10 bg-champagne rounded-full flex items-center justify-center hover:scale-110 transition-transform"
                        title="View Aura" aria-label="View Aura"
                        data-testid={`view-aura-${aura.id}`}
                      >
                        <ExternalLink className="w-5 h-5 text-midnight" />
                      </Link>
                      {!isArchived && (<>
                      <Link
                        to={`/auracast/${aura.aura_key}`}
                        className="w-10 h-10 bg-champagne rounded-full flex items-center justify-center hover:scale-110 transition-transform"
                        title="AuraCast Live" aria-label="AuraCast Live"
                      >
                        <Monitor className="w-5 h-5 text-midnight" />
                      </Link>
                      <a
                        href={`${API_URL}/api/aura-key/${aura.aura_key}/qr`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-10 h-10 bg-champagne rounded-full flex items-center justify-center hover:scale-110 transition-transform"
                        title="View QR Code" aria-label="View QR Code"
                      >
                        <QrCode className="w-5 h-5 text-midnight" />
                      </a>
                      <button
                        onClick={() => handleToggleVault(aura.id)}
                        className={`w-10 h-10 rounded-full flex items-center justify-center hover:scale-110 transition-transform ${isVaulted ? 'bg-aura-purple' : 'bg-champagne/80'}`}
                        title={isVaulted ? 'Remove from Vault' : 'Move to Vault'}
                        data-testid={`vault-toggle-${aura.id}`}
                      >
                        {isVaulted ? <Unlock className="w-5 h-5 text-white" /> : <Lock className="w-5 h-5 text-midnight" />}
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(aura.id)}
                        className="w-10 h-10 bg-error rounded-full flex items-center justify-center hover:scale-110 transition-transform"
                        title="Delete Aura" aria-label="Delete Aura"
                        data-testid={`delete-aura-${aura.id}`}
                      >
                        <Trash2 className="w-5 h-5 text-white" />
                      </button>
                      </>)}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-5">
                    <Link to={`/aura/${aura.id}`}>
                      <h3 className="text-xl font-serif font-medium text-text-light mb-2 hover:text-champagne transition-colors">
                        {aura.title}
                      </h3>
                    </Link>
                    <div className="flex items-center gap-4 text-sm text-text-muted">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(aura)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Image className="w-4 h-4" />
                        {aura.media_count} moments
                      </span>
                    </div>
                    <div className="mt-3 flex items-center gap-2">
                      <span className={`text-xs px-2 py-1 rounded ${
                        aura.privacy_mode === 'open' ? 'bg-success/20 text-success' :
                        aura.privacy_mode === 'locked' ? 'bg-champagne/20 text-champagne' :
                        'bg-aura-purple/20 text-aura-purple'
                      }`}>
                        <span className="inline-flex items-center gap-1">
                          {aura.privacy_mode === 'open' ? <><Globe className="w-3 h-3" /> Open</> :
                           aura.privacy_mode === 'locked' ? <><Lock className="w-3 h-3" /> Locked</> :
                           <><Eye className="w-3 h-3" /> Moderated</>}
                        </span>
                      </span>
                      {isExpired && !isVaulted && (
                        <button
                          onClick={() => handleToggleVault(aura.id)}
                          className="text-xs px-2 py-1 rounded bg-error/20 text-error hover:bg-error/30 transition-colors"
                          data-testid={`archive-vault-${aura.id}`}
                        >
                          Move to Vault
                        </button>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        );
        })()}
      </div>

      {/* API Keys Section — Enterprise only */}
      {user?.plan === 'enterprise' && (
        <div className="max-w-6xl mx-auto px-4 pb-8">
          <div className="aura-card rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-serif font-medium text-text-light flex items-center gap-2">
                <Key className="w-5 h-5 text-champagne" /> API Keys
              </h2>
              <button
                onClick={() => { setShowApiKeys(!showApiKeys); if (!showApiKeys) fetchApiKeys(); }}
                className="btn-outline text-sm px-3 py-2"
              >
                {showApiKeys ? 'Hide' : 'Manage Keys'}
              </button>
            </div>

            {showApiKeys && (
              <div className="space-y-4">
                {/* Generate new key */}
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Key name (e.g. Production App)"
                    value={newKeyName}
                    onChange={e => setNewKeyName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleGenerateKey()}
                    className="aura-input flex-1 text-sm py-2"
                  />
                  <button
                    onClick={handleGenerateKey}
                    disabled={keyLoading || !newKeyName.trim()}
                    className="btn-champagne text-sm px-4"
                  >
                    {keyLoading ? '...' : 'Generate'}
                  </button>
                </div>

                {/* Newly generated key — show once */}
                {newKeyResult && (
                  <div className="bg-champagne/10 border border-champagne/30 rounded-xl p-4">
                    <p className="text-champagne font-medium text-sm mb-1">New API Key — Store this now, it won't be shown again</p>
                    <div className="flex items-center gap-2 mt-2">
                      <code className="text-xs text-text-light bg-midnight/60 px-3 py-2 rounded flex-1 break-all font-mono">{newKeyResult.key}</code>
                      <button
                        onClick={() => handleCopyKey(newKeyResult.key)}
                        className="p-2 text-champagne hover:bg-champagne/10 rounded transition-colors shrink-0"
                      >
                        {keyCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </button>
                    </div>
                    <button onClick={() => setNewKeyResult(null)} className="text-xs text-text-muted mt-2 hover:text-text-light">
                      I've saved it — dismiss
                    </button>
                  </div>
                )}

                {/* Existing keys */}
                {apiKeys.length > 0 ? (
                  <div className="space-y-2">
                    {apiKeys.map(k => (
                      <div key={k.id} className="flex items-center justify-between bg-midnight/50 rounded-lg p-3">
                        <div>
                          <p className="text-sm text-text-light font-medium">{k.name}</p>
                          <p className="text-xs text-text-muted font-mono">{k.prefix}••••••••  ·  Created {new Date(k.created_at).toLocaleDateString()}{k.last_used ? ` · Last used ${new Date(k.last_used).toLocaleDateString()}` : ''}</p>
                        </div>
                        <button
                          onClick={() => handleRevokeKey(k.id)}
                          className="p-2 text-text-muted hover:text-red-400 transition-colors"
                          title="Revoke key" aria-label="Revoke key"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-text-muted text-sm">No active API keys.</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="aura-card rounded-xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto"
          >
            <h3 className="text-xl font-serif font-medium text-text-light mb-2">Delete Aura?</h3>
            <p className="text-text-muted mb-6">
              This will permanently delete the aura and all uploaded moments. This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 btn-outline"
                data-testid="cancel-delete-btn"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteAura(deleteConfirm)}
                className="flex-1 bg-error text-white px-4 py-3 rounded-lg font-medium hover:bg-error/90 transition-colors"
                data-testid="confirm-delete-btn"
              >
                Delete
              </button>
            </div>
          </motion.div>
        </div>
      )}
      <FooterExpanded />
    </div>
  );
}
